import { useMutation, useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

export type ExternalTestLayerStatus =
	| "not_run"
	| "artifact_valid"
	| "artifact_incomplete"
	| "artifact_failed";

export interface EvaluationCriterion {
	id: string;
	label: string;
	question: string;
	tau2_signal: string;
	internal_presentation: string;
}

export interface ExternalTestArtifactRun {
	run_id: string;
	test_layer: string;
	created_at: string;
	artifact_dir: string;
	command: string;
	command_returncode: number | null;
	readiness_status: string | null;
	static_contract_valid: boolean | null;
	human_interpretation_required: boolean;
	tau2_commit: string | null;
	project_commit: string | null;
	missing_required_files: string[];
}

export interface ExternalTestArtifactFile {
	path: string;
	kind: string;
	size_bytes: number;
}

export interface ExternalTestArtifactTextFile {
	path: string;
	preview: string;
	truncated: boolean;
}

export interface ExternalTestArtifactDetailResponse {
	run: ExternalTestArtifactRun;
	summary_text: string;
	decision_text: string;
	command_outputs: ExternalTestArtifactTextFile[];
	files: ExternalTestArtifactFile[];
	admin_execution_enabled: boolean;
	can_write_internal_evaluation: boolean;
}

export interface ExternalTestRunRequestDraftRequest {
	task_set_id: "p0_supported_probe" | "p1_gap_probe";
	test_layer:
		| "adapter_only_static_mapping"
		| "r4_read_only_protected_dry_run_contract_test"
		| "tau2_native_poc_preflight"
		| "offline_tau2_retail_poc";
	objective: string;
	allow_llm_cost: boolean;
	allow_project_agent_runtime: boolean;
	allow_shopify_live_write: boolean;
}

export interface ExternalTestReadinessCheck {
	id: string;
	label: string;
	status: "passed" | "manual_required" | "blocked";
	detail: string;
}

export interface ExternalTestRunRequestDraftResponse {
	draft_status: "manual_review_required";
	task_set_id: string;
	test_layer: string;
	objective: string;
	admin_execution_enabled: boolean;
	ready_for_admin_execution: boolean;
	required_confirmations: string[];
	blocked_actions: string[];
	expected_outputs: string[];
	handoff_documents: string[];
	readiness_checks: ExternalTestReadinessCheck[];
	handoff_summary_markdown: string;
}

export interface ExternalTestLayer {
	test_layer: string;
	label: string;
	phase: string;
	mode: string;
	domain: string;
	artifact_policy: string;
	status: ExternalTestLayerStatus;
	required_files_count: number;
	latest_artifact: ExternalTestArtifactRun | null;
	can_execute_from_admin: boolean;
	requires_manual_confirmation: boolean;
	next_action: string;
}

export interface ExternalTaskSetSummary {
	id: string;
	label: string;
	description: string;
	task_count: number;
	expected_statuses: string[];
	test_modes: string[];
	primary_questions_preview: string[];
}

export interface FeedbackRoute {
	route: string;
	target: string;
	condition: string;
}

export interface ExternalTestIsolationRule {
	id: string;
	label: string;
	purpose: string;
	protected_surface: string;
	allowed_path: string;
	prohibited_actions: string[];
}

export interface ExternalTestBoundaries {
	admin_scope: string;
	allowed_now: string[];
	blocked_now: string[];
	still_prohibited: string[];
	must_not: string[];
}

export interface ExternalTestsOverviewResponse {
	scope: string;
	domain: string;
	current_phase: string;
	ledger_status: string;
	external_testing_decision: string;
	can_start_external_tests: boolean;
	admin_execution_enabled: boolean;
	evaluation_criteria: EvaluationCriterion[];
	task_sets: ExternalTaskSetSummary[];
	test_layers: ExternalTestLayer[];
	latest_runs: ExternalTestArtifactRun[];
	feedback_routes: FeedbackRoute[];
	isolation_strategy: ExternalTestIsolationRule[];
	boundaries: ExternalTestBoundaries;
	source_documents: string[];
	updated_at: string;
}

export function useExternalTestsOverview() {
	return useQuery<ExternalTestsOverviewResponse>({
		queryKey: ["admin", "external-tests", "overview"],
		queryFn: async () => {
			const { data } = await apiClient.get<ExternalTestsOverviewResponse>(
				"/admin/v1/external-tests/overview",
			);
			return data;
		},
	});
}

export function useExternalTestArtifactDetail(runId: string | null) {
	return useQuery<ExternalTestArtifactDetailResponse>({
		queryKey: ["admin", "external-tests", "artifact", runId],
		enabled: runId !== null,
		queryFn: async () => {
			if (runId === null) {
				throw new Error("runId is required");
			}
			const { data } = await apiClient.get<ExternalTestArtifactDetailResponse>(
				`/admin/v1/external-tests/artifacts/${encodeURIComponent(runId)}`,
			);
			return data;
		},
	});
}

export function useExternalTestRunRequestDraft() {
	return useMutation<
		ExternalTestRunRequestDraftResponse,
		Error,
		ExternalTestRunRequestDraftRequest
	>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<ExternalTestRunRequestDraftResponse>(
				"/admin/v1/external-tests/run-requests/draft",
				body,
			);
			return data;
		},
	});
}
